import React, { useState, useEffect } from 'react';
import { 
  Shield, CheckCircle, AlertTriangle, XCircle, Key, Server, 
  Calendar, Users, Settings, RefreshCw, Copy, Download, FlaskConical, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LicenseAdmin = () => {
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [installMode, setInstallMode] = useState(false);
  const [installKey, setInstallKey] = useState({ display: '', encoded: '' });
  const [installing, setInstalling] = useState(false);
  const [fingerprint, setFingerprint] = useState(null);
  // Ankur 29-Jun-2026 — Test Keys Library: pulls fresh fixtures from
  // /api/qa/license-assets/license-fixtures.json so testers don't need to
  // re-download stale Excel/Word files. Bulletproof against pre-iter58 keys.
  const [fixtures, setFixtures] = useState(null);
  const [fixturesLoading, setFixturesLoading] = useState(false);
  const [fixturesError, setFixturesError] = useState(null);
  const [showFixtures, setShowFixtures] = useState(false);

  useEffect(() => {
    fetchLicenseStatus();
  }, []);

  const fetchLicenseStatus = async () => {
    setLoading(true);
    try {
      // Ankur 27-Jun-2026 — bug: 'token' key returns null because the rest
      // of the app stores the JWT under 'hrms_token'. That sent
      // `Authorization: Bearer null` and the backend (correctly) rejected
      // with "Invalid token". Aligning all three reads with the rest of
      // the codebase.
      const token = localStorage.getItem('hrms_token');
      const response = await fetch(`${API_URL}/api/license/admin`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLicense(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to fetch license');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchFingerprint = async () => {
    try {
      const token = localStorage.getItem('hrms_token');
      const response = await fetch(`${API_URL}/api/license/fingerprint`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFingerprint(data);
      }
    } catch (err) {
      console.error('Failed to fetch fingerprint');
    }
  };

  const installLicense = async () => {
    if (!installKey.display || !installKey.encoded) {
      alert('Please enter both license key fields');
      return;
    }

    // Ankur 29-Jun-2026 — client-side guard for instant feedback. The
    // backend re-checks the same regex (Pydantic field_validator on
    // LicenseInstallRequest.license_key_display) so this is purely UX.
    // Pattern: MHO-<2-8 alnum>-XXXX-XXXX-XXXX where each X is from the
    // unambiguous alphabet (no I / L / O / 0 / 1).
    const displayPattern = /^MHO-[A-Z0-9]{2,8}-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/;
    const normalizedDisplay = installKey.display.trim().toUpperCase();
    if (!displayPattern.test(normalizedDisplay)) {
      alert(
        'Invalid License Display Key format.\n\n' +
        'Expected: MHO-<tier>-XXXX-XXXX-XXXX (e.g. MHO-ENT-K3M4-DWHX-YWX7).\n' +
        'Each XXXX block uses the unambiguous alphabet ABCDEFGHJKLMNPQRSTUVWXYZ23456789 — no I, L, O, 0 or 1.'
      );
      return;
    }

    setInstalling(true);
    try {
      const token = localStorage.getItem('hrms_token');
      const response = await fetch(`${API_URL}/api/license/install`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          license_key_display: normalizedDisplay,
          license_key_encoded: installKey.encoded.trim()
        })
      });
      
      if (response.ok) {
        alert('License installed successfully!');
        setInstallMode(false);
        setInstallKey({ display: '', encoded: '' });
        fetchLicenseStatus();
      } else {
        const errorData = await response.json();
        // Pydantic returns 422 with a structured `detail` array. Flatten it
        // so the alert shows a real message instead of "[object Object]".
        let msg = errorData.detail;
        if (Array.isArray(msg)) {
          msg = msg.map(d => d.msg || d.message || JSON.stringify(d)).join('; ');
        } else if (typeof msg !== 'string') {
          msg = JSON.stringify(msg);
        }
        alert(`Failed to install license: ${msg}`);
      }
    } catch (err) {
      alert('Failed to connect to server');
    } finally {
      setInstalling(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard');
  };

  // Ankur 29-Jun-2026 — load fresh test fixtures so testers can install with
  // one click and never paste a stale pre-iter58 key again.
  const loadFixtures = async () => {
    setFixturesLoading(true);
    setFixturesError(null);
    try {
      const resp = await fetch(`${API_URL}/api/qa/license-assets/license-fixtures.json`, {
        cache: 'no-store',
      });
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setFixtures(data);
      setShowFixtures(true);
    } catch (e) {
      setFixturesError(e.message || 'Failed to load fixtures');
    } finally {
      setFixturesLoading(false);
    }
  };

  const applyFixture = (entry) => {
    setInstallMode(true);
    setInstallKey({
      display: entry.display_key || '',
      encoded: entry.encoded_key || '',
    });
    // Smooth scroll to the install form so the prefilled values are visible.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="license-install-display-input"]');
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'valid':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200">
            <CheckCircle className="w-4 h-4 mr-1" /> Valid
          </span>
        );
      case 'grace_period':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200">
            <AlertTriangle className="w-4 h-4 mr-1" /> Grace Period
          </span>
        );
      case 'expired':
      case 'locked':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200">
            <XCircle className="w-4 h-4 mr-1" /> {status === 'locked' ? 'Locked' : 'Expired'}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200">
            {status}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Shield className="w-8 h-8 text-emerald-500 mr-3" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">License Management</h1>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadFixtures}
            disabled={fixturesLoading}
            className="flex items-center px-4 py-2 border border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/30 rounded-lg text-purple-700 dark:text-purple-200 hover:bg-purple-100 dark:hover:bg-purple-900/50 disabled:opacity-50"
            data-testid="license-load-test-keys-btn"
            title="Load fresh test keys (QA only)"
          >
            {fixturesLoading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FlaskConical className="w-4 h-4 mr-2" />
            )}
            Test Keys
          </button>
          <button
            onClick={fetchFingerprint}
            className="flex items-center px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
          >
            <Server className="w-4 h-4 mr-2" />
            View Fingerprint
          </button>
          <button
            onClick={() => setInstallMode(!installMode)}
            className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            data-testid="license-install-toggle"
          >
            <Key className="w-4 h-4 mr-2" />
            {installMode ? 'Cancel' : 'Install License'}
          </button>
        </div>
      </div>

      {/* Test Keys Library — only shows when explicitly loaded. Lets QA
          install fresh keys with one click, bypassing any stale downloaded
          Excel/Word files. */}
      {showFixtures && (
        <div className="mb-6 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4" data-testid="license-test-keys-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <FlaskConical className="w-5 h-5 text-purple-600 dark:text-purple-300 mr-2" />
              <h3 className="font-semibold text-purple-900 dark:text-purple-100">Test Keys Library (QA)</h3>
              <span className="ml-2 text-xs text-purple-700 dark:text-purple-200 bg-purple-100 dark:bg-purple-800/60 px-2 py-0.5 rounded">
                Live — loaded from server
              </span>
            </div>
            <button
              onClick={() => setShowFixtures(false)}
              className="text-purple-700 dark:text-purple-200 hover:text-purple-900 dark:hover:text-white text-sm"
              data-testid="license-test-keys-close"
            >
              Hide
            </button>
          </div>
          {fixturesError && (
            <div className="text-red-700 dark:text-red-300 text-sm mb-2">Error: {fixturesError}</div>
          )}
          {fixtures && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(fixtures).map(([name, entry]) => (
                <div
                  key={name}
                  className="bg-white dark:bg-slate-800 border border-purple-200 dark:border-purple-800 rounded p-3"
                  data-testid={`license-fixture-${name}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 dark:text-slate-100 text-sm">{name}</div>
                      <div className="font-mono text-xs text-gray-700 dark:text-slate-300 mt-1 truncate">
                        {entry.display_key}
                      </div>
                      {entry.payload && (
                        <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                          {entry.payload.license_type && (
                            <span className="capitalize mr-2">{entry.payload.license_type}</span>
                          )}
                          {entry.payload.max_employees !== undefined && (
                            <span>• {entry.payload.max_employees === 9999 ? 'unlimited' : entry.payload.max_employees} emp</span>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => applyFixture(entry)}
                      className="ml-2 flex items-center px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
                      data-testid={`license-fixture-use-${name}`}
                    >
                      <Zap className="w-3 h-3 mr-1" />
                      Use
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 text-xs text-purple-700 dark:text-purple-200">
            These keys are minted with the current iter58 signing tool and include the
            cryptographic display-key binding. Click <strong>Use</strong> on any row to
            prefill the install form.
          </div>
        </div>
      )}

      {/* Warnings Banner */}
      {license?.license?.warnings?.length > 0 && (
        <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 rounded">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-yellow-400 mr-2" />
            <div>
              {license.license.warnings.map((warning, idx) => (
                <p key={idx} className="text-yellow-700 dark:text-yellow-200">{warning}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Install License Form */}
      {installMode && (
        <div className="mb-6 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-slate-100">Install New License</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                License Key (Display)
              </label>
              <input
                type="text"
                value={installKey.display}
                onChange={(e) => setInstallKey({ ...installKey, display: e.target.value })}
                placeholder="MHO-ENT-XXXX-XXXX-XXXX"
                className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-emerald-500"
                data-testid="license-install-display-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                Encoded License Key
              </label>
              <textarea
                value={installKey.encoded}
                onChange={(e) => setInstallKey({ ...installKey, encoded: e.target.value })}
                placeholder="Paste the encoded license key here..."
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-emerald-500 font-mono text-sm"
                data-testid="license-install-encoded-input"
              />
            </div>
            <button
              onClick={installLicense}
              disabled={installing}
              className="flex items-center px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              data-testid="license-install-submit"
            >
              {installing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              {installing ? 'Installing...' : 'Install'}
            </button>
          </div>
        </div>
      )}

      {/* Fingerprint Info */}
      {fingerprint && (
        <div className="mb-6 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-3">Server Fingerprint (for license reissue)</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between bg-white dark:bg-slate-900 p-2 rounded border border-gray-200 dark:border-slate-700">
              <span className="text-sm text-gray-600 dark:text-slate-300">Server: </span>
              <code className="text-xs font-mono text-gray-800 dark:text-slate-100">{fingerprint.server_fingerprint}</code>
              <button onClick={() => copyToClipboard(fingerprint.server_fingerprint)} className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200">
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center justify-between bg-white dark:bg-slate-900 p-2 rounded border border-gray-200 dark:border-slate-700">
              <span className="text-sm text-gray-600 dark:text-slate-300">Database: </span>
              <code className="text-xs font-mono text-gray-800 dark:text-slate-100">{fingerprint.database_fingerprint}</code>
              <button onClick={() => copyToClipboard(fingerprint.database_fingerprint)} className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* License Details */}
      {license?.installed ? (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 dark:bg-slate-900/50 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
            <div className="flex items-center">
              <Key className="w-5 h-5 text-emerald-500 mr-2" />
              <span className="font-semibold text-gray-900 dark:text-slate-100">
                {license.license.license_key_display}
              </span>
            </div>
            {getStatusBadge(license.license.status)}
          </div>
          
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Company Info */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-1">Company</h4>
              <p className="text-lg font-semibold text-gray-900 dark:text-slate-100">{license.license.company_name}</p>
            </div>
            
            {/* License Type */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-1">License Type</h4>
              <p className="text-lg font-semibold text-gray-900 dark:text-slate-100 capitalize">{license.license.license_type}</p>
            </div>
            
            {/* Expiry — Iter63 (Ankur 30-Jun-2026): negative "-31 days
                remaining" was confusing. Now we show a human-readable
                indicator based on status. */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-1">Expires</h4>
              <p className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                {new Date(license.license.expires_at).toLocaleDateString()}
              </p>
              {(() => {
                const lic = license.license;
                const dr = lic.days_remaining;
                if (lic.status === 'locked') {
                  const ago = lic.days_since_expiry ?? Math.abs(dr || 0);
                  return (
                    <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                      Expired {ago} day{ago === 1 ? '' : 's'} ago
                    </p>
                  );
                }
                if (lic.status === 'grace_period') {
                  const grace = lic.grace_days_remaining ?? 0;
                  return (
                    <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                      {grace} day{grace === 1 ? '' : 's'} of grace remaining
                    </p>
                  );
                }
                if (typeof dr === 'number' && dr >= 0) {
                  return (
                    <p className="text-sm text-gray-500 dark:text-slate-400">
                      {dr} day{dr === 1 ? '' : 's'} remaining
                    </p>
                  );
                }
                return null;
              })()}
            </div>
            
            {/* Employee Limit */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-1">Employee Limit</h4>
              <div className="flex items-baseline">
                <span className="text-lg font-semibold text-gray-900 dark:text-slate-100">{license.license.current_employees}</span>
                <span className="text-gray-500 dark:text-slate-400 mx-1">/</span>
                <span className="text-gray-700 dark:text-slate-300">{license.license.max_employees === 9999 ? 'Unlimited' : license.license.max_employees}</span>
              </div>
            </div>
            
            {/* Primary Domain */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-1">Primary Domain</h4>
              <p className="text-gray-900 dark:text-slate-100">{license.license.primary_domain}</p>
            </div>
            
            {/* Staging Domain — Iter62 (Shikha 30-Jun-2026): when no staging
                domain is bound (Trial / Startup tiers), the bare "-" was
                misread as a bug. Show an explicit "Not applicable" caption
                so testers know it's by design. */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-1">Staging Domain</h4>
              {license.license.staging_domain ? (
                <p className="text-gray-900 dark:text-slate-100">{license.license.staging_domain}</p>
              ) : (
                <p
                  className="text-gray-500 dark:text-slate-400 italic"
                  title={`Staging domain is only included in Business and Enterprise tiers. The current ${license.license.license_type} tier does not bundle one.`}
                >
                  Not applicable for {license.license.license_type} tier
                </p>
              )}
            </div>
            
            {/* Fingerprint Status */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-1">Server Binding</h4>
              <p className="text-gray-900 dark:text-slate-100">
                {license.license.fingerprint_bound ? (
                  <span className="text-green-600 dark:text-green-400">Bound</span>
                ) : (
                  <span className="text-yellow-600 dark:text-yellow-400">Not yet bound</span>
                )}
              </p>
            </div>
            
            {/* Last Validated */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-1">Last Validated</h4>
              <p className="text-gray-900 dark:text-slate-100">
                {license.license.last_validated 
                  ? new Date(license.license.last_validated).toLocaleString()
                  : 'Never'
                }
              </p>
            </div>
          </div>
          
          {/* Features */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700">
            <h4 className="text-sm font-medium text-gray-500 dark:text-slate-400 mb-3">Licensed Features</h4>
            <div className="flex flex-wrap gap-2">
              {license.license.features.map((feature, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {feature.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-12 text-center">
          <Shield className="w-16 h-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-2">No License Installed</h3>
          <p className="text-gray-500 dark:text-slate-400 mb-6">
            Click "Install License" above to activate your MyHR Orbit license.
          </p>
          <p className="text-sm text-gray-400 dark:text-slate-500">
            Contact your vendor if you don't have a license key.
          </p>
        </div>
      )}
    </div>
  );
};

export default LicenseAdmin;
